import { useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import i18n from 'i18n-react';
import { Link, Redirect } from 'react-router-dom';

import { FastTrackAnswerFeedback } from 'components/FastTrackAnswerFeedback/FastTrackAnswerFeedback';
import Progress from 'components/Progress';
import TrainingContent from 'components/TrainingContent';

import { fastTrackData } from 'helpers/FastTrack/fastTrackData';
import { legacyRemCss } from 'helpers/legacyRem';

import { useFastTrackQuestions } from 'hooks/useFastTrackQuestions/useFastTrackQuestions';
import { useQuizTimer } from 'hooks/useQuizTimer';

import c from './_fastTrackContentView.module.scss';

const TIMER_DURATION = 60;
const RADIUS = 45;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

interface FastTrackContentViewProps {
  history: {
    push: (path: string) => void;
    replace: (path: string) => void;
  };
  match: {
    params: {
      chapterId: string;
      pageId: string;
    };
  };
}

export function FastTrackContentView({ history, match }: FastTrackContentViewProps) {
  const { chapterId, pageId } = match.params;
  const { pages, ready, appId } = useFastTrackQuestions();

  const [contentCompleted, setContentCompleted] = useState(false);
  const [errors, setErrors] = useState(0);

  const totalErrors = +(fastTrackData<number>('errors') ?? 0);

  // Stop the timer once a question is locked in (either answered or time expired).
  // errors > 0 means a wrong answer was given; contentCompleted means correct.
  const { timeLeft, isExpired, reset } = useQuizTimer({
    duration: TIMER_DURATION,
    active: ready && !contentCompleted && errors === 0,
  });

  // ── Redirect guards ──────────────────────────────────────────────────────
  if (ready) {
    if (fastTrackData<number>('errors') === -1) {
      return <Redirect to="/main/training" />;
    }

    if (fastTrackData<boolean>('finished')) {
      return <Redirect to="/main/training" />;
    }

    const answered = +(fastTrackData<number>('answered') ?? 0);
    if (Number(pageId) !== answered) {
      return <Redirect to={`/main/fasttrack/chapter/${chapterId}/${answered}`} />;
    }
  }

  // ── Navigation ───────────────────────────────────────────────────────────
  const nextPage = () => {
    window.scrollTo(0, 0);
    reset();
    setContentCompleted(false);
    const answered = +(fastTrackData<number>('answered') ?? 0) + 1;
    fastTrackData('answered', answered);

    if (Number(pageId) + 1 < pages.length) {
      history.replace(`/main/fasttrack/chapter/${chapterId}/${Number(pageId) + 1}`);
    } else {
      fastTrackData('finished', true);
      history.push('/main/training');
    }
  };

  // ── Content renderer ─────────────────────────────────────────────────────
  const renderContent = () => {
    // TODO: Array index access is typed as T, not T. Possibly related: noUncheckedIndexAccess in tsconfig, which is tested in glassWidgets. review after merge(s)
    const page = pages[Number(pageId)];
    if (!page) return null;

    const { correctAnswerText } = page;
    const onError = errors >= 1;

    return (
      <TrainingContent
        key={pageId}
        type="fasttrack"
        appId={appId}
        badge=""
        chapterTitle=""
        params={page}
        pageId={pageId}
        LinkButtons={[]}
        quizMode
        quizTimerExpired={isExpired}
        contentComplete={() => {
          // Record before the state updates: with legacy ReactDOM.render, setState in a
          // timeout re-renders synchronously and the header score reads this storage.
          const correctAnswers = fastTrackData<string[]>('correctAnswers') ?? [];
          fastTrackData('correctAnswers', [...correctAnswers, page.UPID]);
          setContentCompleted(true);
          setErrors(0);
        }}
        completedBefore={false}
        correct={(increment: boolean) => {
          if (increment) {
            const correctAnswers = fastTrackData<string[]>('correctAnswers') ?? [];
            fastTrackData('correctAnswers', [...correctAnswers, page.UPID]);
          }
          return null;
        }}
        error={(increment: boolean) => {
          if (increment) {
            setErrors((e) => {
              return e + 1;
            });
            fastTrackData('errors', totalErrors + 1);
          }

          if (!contentCompleted && !onError) return null;

          return (
            <FastTrackAnswerFeedback
              key={`feedback-${pageId}`}
              variant={onError ? 'incorrect' : 'correct'}
              message={onError ? i18n.translate('FastTrackView.incorrectAnswer') : correctAnswerText}
              onNext={() => {
                setErrors(0);
                nextPage();
              }}
            />
          );
        }}
      />
    );
  };

  const correctAnswersCount = fastTrackData<string[]>('correctAnswers')?.length ?? 0;

  return (
    <div className={clsx('fast-track-content-view', c.wrapper)}>
      {ready && (
        <div className={c.fastTrackContentViewProgress}>
          <Progress
            id="training_progress"
            className={c.fastTrackContentViewProgressBar}
            value={Math.round((Number(pageId) / (pages.length - 1)) * 100)}
          />
        </div>
      )}
      {ready && (
        <section className={c.fastTrackContentViewPage} key={pageId}>
          <div className={c.fastTrackContentViewBackBtn}>
            <Link
              to="/main/training"
              className={c.fastTrackContentViewBackBtnLink}
              aria-label={i18n.translate('general.back') as string}
            >
              <FontAwesomeIcon icon={['fas', 'times']} />
            </Link>
          </div>
          <style>{`#openWidget { display: block; bottom: ${legacyRemCss(4)}; }`}</style>
          <section className={c.fastTrackContentViewPanel}>
            <div>
              <div className={c.fastTrackContentViewHeader}>
                <span className={c.fastTrackContentViewHeaderLabel}>
                  {i18n.translate('FastTrackView.question', { current: Number(pageId) + 1, total: pages.length })}
                </span>
                <div className={c.fastTrackContentViewHeaderProgressBar}>
                  <div
                    className={c.fastTrackContentViewHeaderProgressFill}
                    style={{ width: `${((Number(pageId) + 1) / pages.length) * 100}%` }}
                  />
                </div>
                <span className={c.fastTrackContentViewHeaderScore}>
                  {i18n.translate('FastTrackView.score', { correct: correctAnswersCount, total: pages.length })}
                </span>
              </div>
              <div id="parent" className={c.fastTrackContentViewPanelContent}>
                <div className={c.fastTrackContentViewQuizRow}>
                  {(() => {
                    const clampedTime = Math.max(0, timeLeft);
                    const dashOffset = CIRCUMFERENCE - CIRCUMFERENCE * (clampedTime / TIMER_DURATION);
                    const isWarning = clampedTime <= 5;
                    return (
                      <div className={c.fastTrackContentViewTimer}>
                        <svg viewBox="0 0 100 100" className={c.fastTrackContentViewTimerSvg}>
                          <circle cx="50" cy="50" r={RADIUS} className={c.fastTrackContentViewTimerBg} />
                          <circle
                            cx="50"
                            cy="50"
                            r={RADIUS}
                            className={clsx(c.fastTrackContentViewTimerProgress, {
                              [c.fastTrackContentViewTimerProgressWarning]: isWarning,
                            })}
                            style={{ strokeDasharray: CIRCUMFERENCE, strokeDashoffset: dashOffset }}
                          />
                        </svg>
                        <span
                          className={clsx(c.fastTrackContentViewTimerText, {
                            [c.fastTrackContentViewTimerTextWarning]: isWarning,
                          })}
                        >
                          {clampedTime}
                        </span>
                      </div>
                    );
                  })()}
                  <div className={c.fastTrackContentViewQuizQuestion}>
                    {renderContent()}
                    <p className={c.fastTrackContentViewQuizCounter}>
                      {i18n.translate('FastTrackView.questionCounter', {
                        current: Number(pageId) + 1,
                        total: pages.length,
                      })}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </section>
      )}
    </div>
  );
}
